import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        // Verify token is still valid
        const response = await authAPI.getMe();
        const userData = response.data.user || response.data.data;
        
        // Update user data with fresh data from server
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        setIsAuthenticated(true);
        
        // Track last login
        if (userData.lastLogin) {
          const lastLoginDate = new Date(userData.lastLogin);
          const daysSinceLogin = Math.floor((new Date() - lastLoginDate) / (1000 * 60 * 60 * 24));
          
          if (daysSinceLogin > 30) {
            toast.info('Welcome back! It\'s been a while since your last login.');
          }
        }
      } catch (error) {
        // Token is invalid or expired
        if (error.response?.status === 401) {
          // Try to refresh token
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            logout();
          }
        } else {
          logout();
        }
      }
    }
    setLoading(false);
  };

  // Wave-05 · SSO callback support. Accepts a JWT minted by the backend
  // SSO callback and hydrates the auth state without going through the
  // password login endpoint. The callback page uses this after reading
  // the token from the URL fragment.
  const loginWithToken = async (token) => {
    if (!token) throw new Error('No token');
    localStorage.setItem('token', token);
    // Fetch user profile with the new token so we know who they are.
    // authAPI.getProfile reads the token from the axios interceptor.
    try {
      const profileResp = await authAPI.getProfile();
      const user = profileResp?.data?.user || profileResp?.data?.data?.user || profileResp?.data;
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setIsAuthenticated(true);
      }
    } catch (_) {
      // Profile fetch failure — token still stored; ProtectedRoute will
      // fetch on its own next render.
      setIsAuthenticated(true);
    }
    return { success: true };
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const payload = response.data;

      // Wave-12 Phase 1 · TOTP challenge branch. Backend returned a
      // short-lived challenge token instead of the real JWT — surface it
      // to the Login page so it can render the 6-digit code prompt.
      // Auth state stays unset until step 2 succeeds.
      if (payload.twofaRequired) {
        return { success: true, twofaRequired: true, challengeToken: payload.challengeToken };
      }

      const { token, refreshToken, user } = payload;

      // Store tokens and user
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));

      setUser(user);
      setIsAuthenticated(true);

      toast.success('Login successful!');

      // Redirect based on role
      const redirectTo = user.role === 'reviewer' ? '/projects' : '/dashboard';

      const from = location.state?.from?.pathname || redirectTo;
      navigate(from, { replace: true });

      return { success: true, user };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        toast.error('Invalid email or password');
      } else if (error.response?.status === 403) {
        toast.error('Your account has been deactivated. Please contact an administrator.');
      } else {
        toast.error(errorMessage);
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  // Wave-12 Phase 1 · Step-2 of login. Exchanges the challenge token
   // (returned from /auth/login when the user has 2FA enabled) plus a
   // 6-digit TOTP code (or a backup code) for the real access + refresh
   // tokens, then finishes the sign-in exactly like a normal login.
  const verifyTwoFactor = async ({ challengeToken, code }) => {
    try {
      const response = await api.post('/auth/2fa/verify', { challengeToken, code });
      const { token, refreshToken, user, usedBackupCode } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setIsAuthenticated(true);
      toast.success(usedBackupCode ? 'Signed in with backup code' : 'Signed in');
      const redirectTo = user.role === 'reviewer' ? '/projects' : '/dashboard';
      const from = location.state?.from?.pathname || redirectTo;
      navigate(from, { replace: true });
      return { success: true, user };
    } catch (error) {
      const msg = error.response?.data?.error || 'Verification failed';
      toast.error(msg);
      return { success: false, error: msg };
    }
  };

  const register = async (userData) => {
    try {
      // Admin and project managers can register new users
      if (user?.role !== 'super_admin' && user?.role !== 'admin' && user?.role !== 'project_manager') {
        throw new Error('Unauthorized to create users');
      }
      
      // Project managers can only create reviewers
      if (user?.role === 'project_manager' && userData.role !== 'reviewer') {
        throw new Error('Project managers can only create reviewer accounts');
      }
      
      const response = await authAPI.register(userData);
      toast.success('User registered successfully!');
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Registration failed';
      
      // Handle specific error cases
      if (error.response?.data?.error?.includes('already exists')) {
        toast.error('An account with this email already exists');
      } else {
        toast.error(errorMessage);
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const logout = useCallback(() => {
    // Clear all stored data
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('currentOrganizationId');

    setUser(null);
    setIsAuthenticated(false);
    
    // Only show success message if user initiated logout
    if (!location.pathname.includes('/login')) {
      toast.success('Logged out successfully');
    }
    
    navigate('/login');
  }, [navigate, location]);

  const updateProfile = async (userData) => {
    try {
      const response = await authAPI.updateProfile(userData);
      const updatedUser = response.data.data || response.data.user;
      
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      toast.success('Profile updated successfully!');
      return { success: true, data: updatedUser };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Update failed';
      
      if (error.response?.data?.error?.includes('email already')) {
        toast.error('This email is already in use');
      } else {
        toast.error(errorMessage);
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const changePassword = async (currentPasswordOrObject, newPassword) => {
    try {
      // Handle both object and separate parameters
      let passwordData;
      if (typeof currentPasswordOrObject === 'object' && currentPasswordOrObject !== null) {
        passwordData = currentPasswordOrObject;
      } else {
        passwordData = { 
          currentPassword: currentPasswordOrObject, 
          newPassword: newPassword 
        };
      }
        
      await authAPI.changePassword(passwordData);
      toast.success('Password changed successfully!');
      
      // Optional: Force re-login for security
      // setTimeout(() => {
      //   toast.info('Please login with your new password');
      //   logout();
      // }, 2000);
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Password change failed';
      
      if (error.response?.status === 400) {
        toast.error('Current password is incorrect');
      } else {
        toast.error(errorMessage);
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      logout();
      return false;
    }

    try {
      const response = await authAPI.refreshToken(refreshToken);
      const { token, refreshToken: newRefreshToken } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', newRefreshToken);
      
      return true;
    } catch (error) {
      // Refresh token is invalid
      logout();
      return false;
    }
  };

  // Permission check helpers
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    
    const permissions = {
      // Admin permissions (full access)
      admin: [
        'create_project',
        'edit_project',
        'delete_project',
        'assign_reviewers',
        'view_all_projects',
        'manage_all_users',
        'manage_admin_users',
        'manage_project_managers',
        'manage_reviewers',
        'view_all_analytics',
        'view_cost_analytics',
        'manage_mt_settings',
        'manage_system_settings',
        'export_data',
        'manage_glossaries',
        'manage_tm',
        'batch_operations',
        'system_administration'
      ],
      // Project Manager permissions
      project_manager: [
        'create_project',
        'edit_project',
        'delete_project',
        'assign_reviewers',
        'view_all_projects',
        'manage_reviewers',
        'view_analytics',
        'view_own_cost_analytics',
        'export_data',
        'manage_glossaries',
        'manage_tm',
        'batch_operations'
      ],
      // Reviewer permissions
      reviewer: [
        'view_assigned_projects',
        'review_translations',
        'view_glossaries',
        'view_tm',
        'update_profile'
        // NOTE: Reviewers CANNOT export translations
      ]
    };

    return permissions[user.role]?.includes(permission) || false;
  }, [user]);

  const canAccess = useCallback((resource) => {
    if (!user) return false;

    // Super admin and admin have access to everything
    if (user.role === 'super_admin' || user.role === 'admin') {
      return true;
    }

    const accessMap = {
      dashboard: user.role === 'project_manager',
      analytics: user.role === 'project_manager',
      users: user.role === 'project_manager',
      settings: false, // Only admin/super_admin can access MT settings
      batch: user.role === 'project_manager',
      createProject: user.role === 'project_manager',
      allProjects: user.role === 'project_manager',
      assignedProjects: user.role === 'reviewer',
      review: true, // All roles can review
      exportProject: user.role === 'project_manager', // Reviewers CANNOT export
      mtSettings: false, // Admin/super_admin only
      costAnalytics: user.role === 'project_manager'
    };

    return accessMap[resource] || false;
  }, [user]);

  // Update user without API call (for optimistic updates)
  const updateUserLocally = useCallback((updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, [user]);

  // Update user function (alias for updateUserLocally for backward compatibility)
  const updateUser = updateUserLocally;

  const value = {
    user,
    loading,
    isAuthenticated,
    isSuperAdmin: user?.role === 'super_admin',
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isProjectManager: user?.role === 'project_manager',
    isReviewer: user?.role === 'reviewer',
    isManagerOrAdmin: user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'project_manager',
    login,
    loginWithToken,
    verifyTwoFactor,
    logout,
    register,
    updateProfile,
    changePassword,
    refreshAccessToken,
    checkAuth,
    hasPermission,
    canAccess,
    updateUser,
    updateUserLocally,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};